import React from "react";
import {
    SpsTextInput,
    SpsLabel,
    useSpsForm,
    SpsButton,
    usePatchReducer,
} from "@spscommerce/ds-react";
import { ButtonKind, ButtonType } from "@spscommerce/ds-shared";
import { AppRouteProps } from "../../App";
import StatusState from "../../components/StatusState";
import { useSpsLanguage } from "@spscommerce/i18n";

export type ResetPasswordProps = Partial<AppRouteProps> &
    Pick<AppRouteProps, "authService" | "redirectService">;

export function ResetPassword({ authService, redirectService }: ResetPasswordProps) {
    const { t } = useSpsLanguage();
    const [state, patchState] = usePatchReducer<{
        internalUserError: boolean;
        reset: boolean;
        resetError: boolean;
        spinning: boolean;
    }>({
        internalUserError: false,
        reset: false,
        resetError: false,
        spinning: false,
    });

    const { formValue, formMeta } = useSpsForm({
        emailAddress: "",
    });

    function backToLogin() {
        redirectService.backToLogin();
    }

    async function sendResetEmail(e: React.FormEvent) {
        e.preventDefault();
        patchState({ resetError: false, internalUserError: false, spinning: true });
        try {
            await authService.sendPasswordResetEmail(formValue.emailAddress);
            patchState({ reset: true, spinning: false });
        } catch (e) {
            // check for internal SPS user PW change error first
            if (e.response.status === 400 && e.response.data.error === "externally_managed_user") {
                patchState({ internalUserError: true });
            } else {
                // errors not related to internal SPS users
                patchState({ resetError: true });
                console.log(e.response.data.error_description);
            }
        }
        patchState({ spinning: false });
    }

    return (
        <>
            <div className="auth-box">
                <div className="auth-logo">
                    <div className="logo"></div>
                </div>
                <div className="auth-content">
                    {state.reset ? (
                        <>
                            <StatusState
                                header={t("cpauth.resetPassword.instructionsSent", {
                                    defaultValue: "Instructions sent!",
                                })}
                                message={t("cpauth.resetPassword.checkInbox", {
                                    defaultValue:
                                        "Check your inbox for a link to create a new password",
                                })}
                                backToLogin={backToLogin}
                                imgSrc="/assets/image-success.svg"
                                alt="Success Status Icon"
                            />
                        </>
                    ) : (
                        <>
                            <h1 className="auth-content-header">
                                {t("cpauth.resetPassword", { defaultValue: "Reset Password" })}
                            </h1>
                            <p>
                                {t("cpauth.resetPassword.enterEmail", {
                                    defaultValue:
                                        "Enter your email address and we will send you an email to reset your password",
                                })}
                            </p>
                            {state.internalUserError && (
                                <p className="auth-error">
                                    {t("cpauth.resetPassword.spsEmployeeError", {
                                        defaultValue:
                                            "SPS employees and contractors must contact the help desk for password assistance",
                                    })}
                                </p>
                            )}
                            {state.resetError && (
                                <p className="auth-error">
                                    {t("cpauth.resetPassword.defaultError", {
                                        defaultValue: "An error occurred during password reset",
                                    })}
                                </p>
                            )}
                            <form onSubmit={sendResetEmail}>
                                <SpsLabel for={formMeta.fields.emailAddress}>
                                    {t("cpauth.email", { defaultValue: "Email Address" })}
                                </SpsLabel>
                                <SpsTextInput
                                    value={formValue.emailAddress}
                                    formMeta={formMeta.fields.emailAddress}
                                    data-testid="email-input"
                                />
                                {state.spinning ? (
                                    <SpsButton
                                        className="mr-1 mb-1 auth-button"
                                        spinning
                                        kind={ButtonKind.CONFIRM}
                                    >
                                        {t("cpauth.resetPassword.sendNew", {
                                            defaultValue: "Send New Password",
                                        })}
                                    </SpsButton>
                                ) : (
                                    <SpsButton
                                        className="mr-1 mb-1 auth-button"
                                        kind={ButtonKind.CONFIRM}
                                        data-testid="send-button"
                                        disabled={formValue.emailAddress === ""}
                                        type={ButtonType.SUBMIT}
                                    >
                                        {t("cpauth.resetPassword.sendNew", {
                                            defaultValue: "Send New Password",
                                        })}
                                    </SpsButton>
                                )}
                            </form>
                            <br />
                            <SpsButton
                                className="mr-1 mb-1 auth-back-button"
                                kind={ButtonKind.LINK}
                                onClick={backToLogin}
                            >
                                {t("cpauth.goBackToLogin", {
                                    defaultValue: "Go back to Login",
                                })}
                            </SpsButton>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
